'use client';

import { Heading } from '@petsmart-ui/sparky';
import { useEffect, useMemo, useState } from 'react';

import type { ProductVariant } from '@/entities/product';

import type { MappedFBTProduct } from '../lib';
import { useFBTAddToCart } from '../model/useFBTAddToCart';
import { useRecommendedProducts } from '../model/useRecommendedProducts';
import { FBTProductImagesTotal } from './FBTProductImagesTotal';
import { FBTProductList } from './FBTProductList';

export interface FBTFormProps {
    storeId: string;
    selectedProduct: ProductVariant;
}

const MIN_RECOMMENDATIONS = 3;

export function FBTForm({ storeId, selectedProduct }: Readonly<FBTFormProps>) {
    const [selectedProductIds, setSelectedProductIds] = useState<Record<string, boolean>>({});
    const products = useRecommendedProducts({ storeId, product: selectedProduct });
    const { addItemsToCart, isPending } = useFBTAddToCart(storeId);

    const mappedProducts = useMemo(() => products.slice(0, 8), [products]);

    async function handleAddToCart() {
        const productsToAdd = mappedProducts.filter(product => selectedProductIds[product.id]);
        await addItemsToCart(productsToAdd);
    }

    function handleToggleSelectedProduct(product: MappedFBTProduct, checked: boolean) {
        setSelectedProductIds(value => ({ ...value, [product.id]: checked }));
    }

    const productStateKey = useMemo(
        () => mappedProducts.map(product => `${product.id}-${product.inStock}`).join(','),
        [mappedProducts]
    );

    useEffect(() => {
        setSelectedProductIds(
            // biome-ignore lint/performance/noAccumulatingSpread: shorthand
            mappedProducts.reduce((result, product) => ({ ...result, [product.id]: product.inStock }), {})
        );
    }, [productStateKey, mappedProducts]);

    if (mappedProducts.length < MIN_RECOMMENDATIONS) {
        return null;
    }

    return (
        <div data-testid="test-frequently-bought-together" className="fbt sparky-u-margin-top-lg">
            <Heading tagName="h2" size="title">
                Frequently bought together
            </Heading>
            <FBTProductImagesTotal
                products={mappedProducts}
                selectedProductIds={selectedProductIds}
                onAddToCart={handleAddToCart}
                isAddingToCart={isPending}
            />
            <FBTProductList
                products={mappedProducts}
                selectedProductIds={selectedProductIds}
                onToggleSelectedProduct={handleToggleSelectedProduct}
            />
        </div>
    );
}
